import {
  AmountDifferenceOperatorThresholdLabelModifier,
  AmountDifferenceOperatorTrendLabelModifier,
  TrendThresholdBudgetTypeAndAmountLabelModifier,
  TrendThresholdValueMap,
  amountDifferenceOperator,
  type amountDifferenceUnit,
  trendThresholdBudgetTypeAndAmountOptions,
} from '@/features/project/_models/project-filter';
import { PS_MAX_CASCADER_SELECTION } from '@/features/project/review-sheet/utils/constants';
import type {
  ProjectSearchFormType,
  amountSearchTypeEnum,
} from '@/features/search/_models/projects-search';
import { t } from '@/i18n';
import { FALLBACK_TEXT, formatNumber, isPS } from '@/utils';
import type { UseFormSetError, UseFormSetValue } from 'react-hook-form';
import type { z } from 'zod';
import type { ProjectsSearchQueryStates } from '../_hooks/projects-search';
import type { AnalysisSearchFormType } from '../_models/analysis-search';

export const PROJECT_SEARCH_TAG_TEST_ID = 'projects-search-field-tags';

export type SharedSearchFormType =
  | ProjectSearchFormType
  | AnalysisSearchFormType;

export type SharedSearchFormKeys = Extract<
  keyof ProjectSearchFormType & keyof AnalysisSearchFormType,
  string
>;

export type ProjectsSearchAccordionProviderType =
  | 'project'
  | 'group'
  | 'analysis';

export type InputCascaderAndAccountingLabels = {
  nestedOrganizationIds: string[];
  nestedOtherResponsibleOrganizationIds: string[];
  nestedPolicyIds: string[];
  nestedExpenditureBudgetPurposeIds: string[];
  accountingTypeIds: string[];
};

export type ProjectsSearchSubmitType = {
  values: ProjectSearchFormType;
  newCascadeId: string | undefined | null;
  inputCascaderAndAccountingLabels: InputCascaderAndAccountingLabels;
};

export const PERCENT_VALUE_KEYS = [
  trendThresholdBudgetTypeAndAmountOptions.Enum.executionRate,
];
export const COL_MIN_WIDTH = {
  basic: 200,
  large: 400,
} as const;

export const getSearchKeyword = (
  q: string | null,
  isSearchTargetProjectName: boolean | null,
): string | null => {
  if (!q) {
    return null;
  }

  return `${q}${isSearchTargetProjectName ? `（${t('project-name')}）` : ''}`;
};

export const handleCascaderOnChange = (
  key:
    | 'nestedOrganizationIds'
    | 'nestedPolicyIds'
    | 'nestedOtherResponsibleOrganizationIds'
    | 'nestedExpenditureBudgetPurposeIds',
  value: string[][],
  setValue: UseFormSetValue<ProjectSearchFormType | AnalysisSearchFormType>,
  openMessage: (type: 'warning', message: string) => void,
) => {
  const selectionLength = value?.length ?? 0;
  if (isPS() && selectionLength > PS_MAX_CASCADER_SELECTION) {
    openMessage(
      'warning',
      t('warning-message-cascader-selection', {
        limit: PS_MAX_CASCADER_SELECTION,
      }),
    );
  } else {
    setValue(key, value);
  }
};

export const resetAnalyticTrendFields = (
  setValue: UseFormSetValue<ProjectSearchFormType>,
  index: number,
) => {
  setValue(
    `amountSearchConditions.${index}.amountSearchBaseYearWithBudgetType`,
    null,
  );
  setValue(
    `amountSearchConditions.${index}.amountSearchBaseYearWithBudgetTypeEnd`,
    null,
  );
  setValue(`amountSearchConditions.${index}.amountSearchDiffAmount`, null);
  setValue(
    `amountSearchConditions.${index}.amountSearchDiffAmountUnit`,
    'amount',
  );
  setValue(`amountSearchConditions.${index}.amountSearchDiffOperator`, null);
};

const _shouldClearErrorsOrDisableField = (
  operator: z.infer<typeof amountDifferenceOperator> | null,
  amountSearchType: z.infer<typeof amountSearchTypeEnum> | null,
) => {
  return (
    (amountSearchType === 'trend' &&
      operator === amountDifferenceOperator.Values.eq) ||
    operator === amountDifferenceOperator.Values.none
  );
};

export const getAmountSearchTagLabel = ({
  amountSearchType,
  amountSearchBaseYearWithBudgetType,
  amountSearchBaseYearWithBudgetTypeEnd,
  amountSearchDiffAmount,
  amountSearchDiffAmountUnit,
  amountSearchDiffOperator,
}: {
  amountSearchType: z.infer<typeof amountSearchTypeEnum> | null;
  amountSearchBaseYearWithBudgetType: (string | number)[] | null;
  amountSearchBaseYearWithBudgetTypeEnd: (string | number)[] | null | undefined;
  amountSearchDiffAmount: number | null;
  amountSearchDiffAmountUnit: z.infer<typeof amountDifferenceUnit> | null;
  amountSearchDiffOperator: z.infer<typeof amountDifferenceOperator> | null;
}) => {
  if (
    !amountSearchType ||
    !amountSearchBaseYearWithBudgetType ||
    !amountSearchDiffAmountUnit ||
    !amountSearchDiffOperator
  ) {
    return null;
  }
  const formattedAmountSearchDiffAmount = formatNumber(amountSearchDiffAmount);
  const amountValueWithUnit = {
    percent: `${formattedAmountSearchDiffAmount} ${t('percentage-unit')}`,
    amount: t('amount-with-thousand-yen-unit', {
      amount: formattedAmountSearchDiffAmount,
    }),
    fraction: `${formattedAmountSearchDiffAmount} ${t('fraction')}`,
  };

  const isAmountSearchDiffOperatorNone =
    amountSearchDiffOperator === amountDifferenceOperator.Values.none;
  const isAmountSearchDiffOperatorEqual =
    amountSearchDiffOperator === amountDifferenceOperator.Values.eq;
  const differenceOperatorLabel = amountSearchDiffOperator
    ? amountSearchType === 'threshold'
      ? AmountDifferenceOperatorThresholdLabelModifier[amountSearchDiffOperator]
      : AmountDifferenceOperatorTrendLabelModifier[amountSearchDiffOperator]
    : '';
  const budgetAmountVal = amountSearchDiffAmountUnit
    ? amountValueWithUnit[amountSearchDiffAmountUnit]
    : '';

  const isAmountSearchDiffOperatorNoneOrEqual =
    (amountSearchType === 'threshold' && isAmountSearchDiffOperatorNone) ||
    (amountSearchType === 'trend' && isAmountSearchDiffOperatorEqual);

  if (
    !isAmountSearchDiffOperatorNoneOrEqual &&
    formattedAmountSearchDiffAmount === FALLBACK_TEXT
  ) {
    return null;
  }

  const amountWithOperatorLabel = isAmountSearchDiffOperatorNoneOrEqual
    ? differenceOperatorLabel
    : `${budgetAmountVal} ${differenceOperatorLabel}`;
  const [amountSearchBaseFiscalYear, amountSearchBaseBudgetTypeWithAmountType] =
    amountSearchBaseYearWithBudgetType ?? [];

  const baseBudgetTypeWithAmountType: string =
    amountSearchBaseBudgetTypeWithAmountType
      ? TrendThresholdBudgetTypeAndAmountLabelModifier[
          amountSearchBaseBudgetTypeWithAmountType
        ]
      : '';

  if (amountSearchType === 'threshold') {
    // 閾値
    return `${amountWithOperatorLabel} | ${amountSearchBaseFiscalYear} | ${baseBudgetTypeWithAmountType}`;
  }

  const [
    amountSearchTargetFiscalYear,
    amountSearchTargetBudgetTypeWithAmountType,
  ] = amountSearchBaseYearWithBudgetTypeEnd ?? [];

  const targetBudgetTypeWithAmountType: string =
    amountSearchTargetBudgetTypeWithAmountType
      ? TrendThresholdBudgetTypeAndAmountLabelModifier[
          amountSearchTargetBudgetTypeWithAmountType
        ]
      : '';

  // 増減
  return `${amountWithOperatorLabel} | ${amountSearchBaseFiscalYear} ${baseBudgetTypeWithAmountType} - ${amountSearchTargetFiscalYear} ${targetBudgetTypeWithAmountType}`;
};

export const getAmountSearchBaseBudgetTypeAndAmountData = ({
  amountTargetFiscalYearParam,
  budgetType,
  budgetAmount,
}: {
  amountTargetFiscalYearParam: string | null;
  budgetType: string | null;
  budgetAmount: string | null;
}) => {
  if (!amountTargetFiscalYearParam) {
    return null;
  }
  const targetKey = Object.keys(TrendThresholdValueMap).find((key) => {
    if (
      TrendThresholdValueMap[key].budgetType === budgetType &&
      TrendThresholdValueMap[key].budgetAmount === budgetAmount
    ) {
      return key;
    }
    return null;
  });

  if (!targetKey) {
    return null;
  }

  return [amountTargetFiscalYearParam, targetKey];
};

export const validateProjectNumber = (
  values: SharedSearchFormType,
  setError:
    | UseFormSetError<ProjectSearchFormType>
    | UseFormSetError<AnalysisSearchFormType>,
) => {
  const { projectNumbers } = values;
  const isHalfWidthNumeric = /^[0-9]+$/.test(projectNumbers ?? '');
  if (projectNumbers && !isHalfWidthNumeric) {
    setError('projectNumbers', {
      message: t('validation.half-width-numeric-required'),
    });
    return { hasError: true };
  }
  return { hasError: false };
};

// 任意項目だが初期値の設定が必要な値を取得
export const getInitialValueOfOptionalFields = ({
  searchQueryStates,
  initialFiscalYearByGroup,
}: {
  searchQueryStates: ProjectsSearchQueryStates;
  initialFiscalYearByGroup: string | undefined;
}) => {
  // 意図的に未設定にしているのか、queryが設定されていないのか判定するためにlocation.searchを使用

  return {
    fiscalYear: initialFiscalYearByGroup ?? searchQueryStates.fiscalYear,
    skipAdministrativeReformTeamEvaluation: isPS()
      ? null
      : searchQueryStates.skipAdministrativeReformTeamEvaluation === 'all'
        ? null
        : searchQueryStates.skipAdministrativeReformTeamEvaluation,
  };
};
